import { Link } from 'react-router-dom';
import { PS, T } from '../../lib/theme';
import { isFrozenBySeriesEdit, seriesOccurrenceEditSkipLabel } from '../../lib/tournament-series';
import type { SeriesOccurrenceEditOutcome } from '../../lib/types';

interface Props {
  updated: SeriesOccurrenceEditOutcome[];
  skipped: SeriesOccurrenceEditOutcome[];
}

/**
 * The report from `PATCH /admin/tournament-series/{id}` (api#156): which
 * already-materialized occurrences the content edit applied to, and which it
 * left alone. Three visually distinct groups, not a flat list — the "frozen"
 * group (an occurrence with participant history) is the one AC#67 calls out
 * by name: it must read as "go manage this one individually," not as a
 * generic skip, and it links straight to the one-off page that can actually
 * edit it. Both `updated`/`skipped` and *which* skip reason is "frozen" are
 * read directly off the API response (`reason`) — see
 * `lib/tournament-series.ts`'s `isFrozenBySeriesEdit` doc.
 */
export function SeriesEditOutcome({ updated, skipped }: Props) {
  const frozen = skipped.filter((o) => isFrozenBySeriesEdit(o.reason ?? ''));
  const otherSkipped = skipped.filter((o) => !isFrozenBySeriesEdit(o.reason ?? ''));

  if (updated.length === 0 && skipped.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {updated.length > 0 && (
        <OutcomeGroup
          title={`${updated.length} occurrence${updated.length === 1 ? '' : 's'} updated`}
          bg={PS.successSoft}
          border={PS.success}
          text={PS.nearBlack}
          rows={updated}
        />
      )}

      {frozen.length > 0 && (
        <section
          style={{
            padding: 12,
            background: PS.errorBg,
            border: `1px solid ${PS.errorBorder}`,
            borderRadius: 3,
          }}
        >
          <div style={{ ...T.subheading, color: PS.errorText, marginBottom: 4 }}>
            {frozen.length} occurrence{frozen.length === 1 ? '' : 's'} frozen — not bulk-editable
          </div>
          <p style={{ ...T.caption, color: PS.errorText, marginTop: 0, marginBottom: 8 }}>
            These have participant history, so this bulk edit left them completely unchanged. Open
            each one to edit it individually.
          </p>
          <ul style={{ margin: 0, paddingLeft: 18, ...T.caption, color: PS.errorText }}>
            {frozen.map((o) => (
              <li key={o.tournament_id} style={{ marginBottom: 4 }}>
                {o.date} —{' '}
                <Link to={`/tournaments/${o.tournament_id}`} style={{ color: PS.linkBlue }}>
                  Tournament #{o.tournament_id} →
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {otherSkipped.length > 0 && (
        <OutcomeGroup
          title={`${otherSkipped.length} occurrence${otherSkipped.length === 1 ? '' : 's'} skipped`}
          bg={PS.surfaceDisabled}
          border={PS.border}
          text={PS.textSec}
          rows={otherSkipped}
        />
      )}
    </div>
  );
}

function OutcomeGroup({
  title,
  bg,
  border,
  text,
  rows,
}: {
  title: string;
  bg: string;
  border: string;
  text: string;
  rows: SeriesOccurrenceEditOutcome[];
}) {
  return (
    <section style={{ padding: 12, background: bg, border: `1px solid ${border}`, borderRadius: 3 }}>
      <div style={{ ...T.subheading, color: text, marginBottom: 8 }}>{title}</div>
      <ul style={{ margin: 0, paddingLeft: 18, ...T.caption, color: text }}>
        {rows.map((o) => (
          <li key={o.tournament_id} style={{ marginBottom: 4 }}>
            {o.date} —{' '}
            <Link to={`/tournaments/${o.tournament_id}`} style={{ color: PS.linkBlue }}>
              Tournament #{o.tournament_id} →
            </Link>
            {o.reason && <> ({seriesOccurrenceEditSkipLabel(o.reason)})</>}
          </li>
        ))}
      </ul>
    </section>
  );
}
